// pipe scan-intake (track D): a PURE, dependency-free Code 128 encoder for on-screen/print SVG
// barcodes (the ZPL path in zpl.ts uses the printer's native ^BC encoder instead). Follows the
// pure-module style of desktop/pages/workspace/erp-table-filter.ts — no React, no DOM, no I/O.
//
// Symbology recap (ISO/IEC 15417): every symbol is 3 bars + 3 spaces spanning 11 modules (the stop
// adds a final 2-module termination bar → 13). A symbol value is 0–102 for data, 103–105 for the
// start codes (A/B/C) and 106 for the stop. The checksum is
//   (startValue + Σ dataValue_i × i) mod 103   (i = 1-based data position)
// and is inserted between the data and the stop. A quiet zone of ≥ 10 modules flanks each side.

/** Minimum quiet-zone width (modules) each side of the symbol, included in `totalModules`. */
export const QUIET_ZONE_MODULES = 10;

// Module-width patterns indexed by symbol value (0–106). Each entry is the alternating
// bar/space widths, bar first — e.g. value 0 = '212222' → bar 2, space 1, bar 2, space 2, bar 2,
// space 2. Transcribed from the canonical Code 128 table; the spec pins structural invariants
// (6 widths summing to 11 modules with even bar parity, stop = 2331112) plus hand-computed golden
// vectors for known strings.
export const CODE128_PATTERNS: readonly string[] = [
  // 0–15 (set B: SP ! " # $ % & ' ( ) * + , - . /)
  '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312',
  '132212', '221213', '221312', '231212', '112232', '122132', '122231', '113222',
  // 16–31 (set B: 0–9 : ; < = > ?)
  '123122', '123221', '223211', '221132', '221231', '213212', '223112', '312131',
  '311222', '321122', '321221', '312212', '322112', '322211', '212123', '212321',
  // 32–47 (set B: @ A–O)
  '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
  '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121',
  // 48–63 (set B: P–Z [ \ ] ^ _)
  '313121', '211331', '231131', '213113', '213311', '213131', '311123', '311321',
  '331121', '312113', '312311', '332111', '314111', '221411', '431111', '111224',
  // 64–79 (set B: ` a–o)
  '111422', '121124', '121421', '141122', '141221', '112214', '112412', '122114',
  '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
  // 80–95 (set B: p–z { | } ~ DEL)
  '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112',
  '421211', '212141', '214121', '412121', '111143', '111341', '131141', '114113',
  // 96–106 (FNC3 FNC2 Shift CodeC CodeB/FNC4 CodeA/FNC4 FNC1 StartA StartB StartC Stop)
  '114311', '411113', '411311', '113141', '114131', '311141', '411131', '211412',
  '211214', '211232', '2331112',
];

const START_B = 104;
const START_C = 105;
const CODE_TO_C = 99; // "switch to set C" symbol, from set B
const CODE_TO_B = 100; // "switch to set B" symbol, from set C
const STOP = 106;

/** The smallest digit run worth switching to code set C for (per the intake-label spec). */
const MIN_CODE_C_RUN = 6;

export interface Code128Encoding {
  /** Every emitted symbol value in order: start, data (+ code-set switches), checksum, stop. */
  symbols: number[];
  /** The mod-103 check symbol value (also present in `symbols`, before the stop). */
  checksum: number;
  /** Alternating bar/space module widths, bar first; starts and ends on a bar (the stop's
   *  termination bar). Quiet zones are NOT in this array — see `totalModules`. */
  widths: number[];
  /** Total symbol width in modules including both quiet zones. */
  totalModules: number;
}

/** Throws unless `text` is non-empty printable ASCII (32–126) — the character range this encoder
 *  (code set B + digit pairs in C) and the ZPL ^BC field both accept. */
export function assertCode128Encodable(text: string): void {
  if (text.length === 0) {
    throw new Error('code128: cannot encode an empty string');
  }
  for (let i = 0; i < text.length; i++) {
    const cc = text.charCodeAt(i);
    if (cc < 32 || cc > 126) {
      throw new Error(
        `code128: unencodable character at index ${i} — only printable ASCII (32–126) is supported`
      );
    }
  }
}

const digitRunAt = (text: string, at: number): number => {
  let n = 0;
  while (at + n < text.length) {
    const cc = text.charCodeAt(at + n);
    if (cc < 48 || cc > 57) break;
    n++;
  }
  return n;
};

/** Encode `text` as Code 128: code set B by default, switching to the double-density set C for
 *  maximal digit runs of ≥ 6 (an odd-length run emits its leading digit in B so the C pairs stay
 *  even). Start code, checksum and stop are appended automatically. */
export function encode(text: string): Code128Encoding {
  assertCode128Encodable(text);

  const symbols: number[] = [];
  let set: 'B' | 'C' | null = null;
  const ensure = (target: 'B' | 'C') => {
    if (set === target) return;
    if (set === null) symbols.push(target === 'B' ? START_B : START_C);
    else symbols.push(target === 'B' ? CODE_TO_B : CODE_TO_C);
    set = target;
  };

  let i = 0;
  while (i < text.length) {
    let run = digitRunAt(text, i);
    if (run >= MIN_CODE_C_RUN) {
      if (run % 2 === 1) {
        // Odd run: encode the leading digit in B so the remaining run pairs up evenly.
        ensure('B');
        symbols.push(text.charCodeAt(i) - 32);
        i++;
        run--;
      }
      ensure('C');
      for (; run > 0; run -= 2, i += 2) {
        symbols.push(Number(text.slice(i, i + 2)));
      }
    } else {
      ensure('B');
      symbols.push(text.charCodeAt(i) - 32);
      i++;
    }
  }

  // Mod-103 checksum: start value + each data/switch symbol weighted by its 1-based position.
  let sum = symbols[0];
  for (let k = 1; k < symbols.length; k++) {
    sum += symbols[k] * k;
  }
  const checksum = sum % 103;
  symbols.push(checksum, STOP);

  const widths: number[] = [];
  for (const symbol of symbols) {
    for (const ch of CODE128_PATTERNS[symbol]) {
      widths.push(Number(ch));
    }
  }
  const totalModules =
    widths.reduce((acc, w) => acc + w, 0) + 2 * QUIET_ZONE_MODULES;

  return { symbols, checksum, widths, totalModules };
}

export interface Code128Svg {
  /** `0 0 <totalModules> <height>` — the x axis is in modules, so any rendered width scales all
   *  bars uniformly (relative widths, which is what a scanner reads, are preserved). */
  viewBox: string;
  /** One `M…Z` rect subpath per bar, quiet zones offsetting the first bar. Render with
   *  `fill` + `shape-rendering="crispEdges"`; spaces are simply unpainted background. */
  path: string;
}

/** Build a crisp single-`<path>` SVG rendering of `encode(text)`.
 *  `height` is in the same unit as the module x axis (default 40). */
export function toSvgPath(
  text: string,
  options?: { height?: number }
): Code128Svg {
  const height = options?.height ?? 40;
  const { widths, totalModules } = encode(text);
  const segments: string[] = [];
  let x = QUIET_ZONE_MODULES;
  let bar = true;
  for (const w of widths) {
    if (bar) segments.push(`M${x} 0h${w}v${height}h-${w}Z`);
    x += w;
    bar = !bar;
  }
  return { viewBox: `0 0 ${totalModules} ${height}`, path: segments.join('') };
}
